import { Flex } from "@chakra-ui/react";
import { ColumnDef } from "@tanstack/react-table";
import { useFlags } from "flagsmith/react";
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { CustomColumnsTable } from "src/components/Table/CustomColumnsTable";
import {
  FILTER_DELIMITER,
  FormTabsTypes,
  SearchAndFilterTypes,
} from "src/constants";
import { useDefaultColumnsProps } from "src/hooks/useDefaultColumnsProps";
import { useGlossary } from "src/hooks/useGlossary";
import { Selection } from "src/hooks/useMultiselectState";
import { useOrganization } from "src/hooks/useOrganization";
import { checkboxColumnDef } from "src/hooks/useTableSelection";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { AdvancedViewSettings } from "./advancedViewSettings/AdvancedViewSettings";
import { TableBanner } from "./components/TableBanner";
import { MenuItem } from "./components/menuItem";
import {
  buildColumnsForTab,
  getDefaultColumnIdsForTab,
  hasAdditionalColumns,
} from "./formTables/columns";
import {
  FormIdListResult,
  FormList,
  FormListRow,
  SchoolFormKeyRecord,
} from "./types";

type Props = {
  enrollmentPeriodId: uuid;
  offset: number;
  limit: number;
  onFetchAllIds: () => Promise<FormIdListResult>;
  onFetchMore: (limit: number, offset: number) => void;
  onRefetch: () => Promise<unknown>;
  dropoffFormTemplateId: uuid;
  previousFormTemplateRemoteData: RD.RemoteData<
    unknown,
    GQL.FormTemplateFragment
  >;
  onSelectionChange: (
    selection: Selection<SchoolFormKeyRecord, FormListRow>
  ) => void;
  selection: Selection<SchoolFormKeyRecord, FormListRow>;
  remoteData: RD.RemoteData<unknown, FormList>;
};
export const DropoffFormsList: React.FC<Props> = (props) => {
  const {
    onFetchAllIds,
    onRefetch,
    enrollmentPeriodId,
    remoteData,
    dropoffFormTemplateId,
    previousFormTemplateRemoteData,
    limit,
    offset,
    onFetchMore,
    onSelectionChange,
    selection,
  } = props;
  const { glossary } = useGlossary();
  const organization = useOrganization();
  const count = remoteData.toNullable()?.totalCount ?? 0;

  const flags = useFlags([
    "saved-view-custom-columns",
    "current-applying-schools",
  ]);
  const isCustomColumnsEnabled = flags["saved-view-custom-columns"].enabled;
  const isCurrentApplyingSchoolsEnabled =
    flags["current-applying-schools"].enabled;

  const formTemplate = previousFormTemplateRemoteData.toNullable() ?? null;
  const defaultColumnsProps = useDefaultColumnsProps({
    formTemplate,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const additionalColumnsIncluded = useMemo(() => {
    return hasAdditionalColumns(searchParams, defaultColumnsProps);
  }, [searchParams, defaultColumnsProps]);

  const handleClearSelection = useCallback(() => {
    onSelectionChange(selection.clear());
  }, [onSelectionChange, selection]);

  const columns: ColumnDef<FormListRow>[] = [
    checkboxColumnDef({
      count,
      id: "Selection",
      onSelectionChange,
      selection,
    }),
    ...buildColumnsForTab({
      tabType: FormTabsTypes.DropoffForms,
      additionalColumnsIncluded,
      glossary,
      organization,
      formTemplate,
      isCurrentApplyingSchoolsEnabled,
      ...defaultColumnsProps,
    }),
  ];

  return (
    <Flex direction="column" gap="4">
      {remoteData.hasError() && <GenericError />}
      <CustomColumnsTable<FormListRow>
        data={remoteData.toNullable()?.rows ?? []}
        columns={columns}
        showAdvancedViewSettingsOnly={additionalColumnsIncluded}
        requiredColumnIds={["Selection"]}
        resourceName={`dropoff-form-list-${dropoffFormTemplateId}`}
        isLoading={
          remoteData.isLoading() || previousFormTemplateRemoteData.isLoading()
        }
        sortableColumnIds={[]}
        count={count}
        limit={limit}
        offset={offset}
        onFetchMore={onFetchMore}
        banner={() => (
          <RemoteDataView
            error={() => <GenericError />}
            remoteData={previousFormTemplateRemoteData}
          >
            {(formTemplate) => (
              <TableBanner
                hiddenMenuItems={[
                  MenuItem.Offers,
                  MenuItem.BulkVerify,
                  MenuItem.BulkStatusUpdate,
                  MenuItem.BulkStatusDescriptionUpdate,
                ]}
                dropoffFormTemplateId={dropoffFormTemplateId}
                formTemplate={formTemplate}
                enrollmentPeriodId={enrollmentPeriodId}
                includeUpdateStatus={false}
                onFetchAllIds={onFetchAllIds}
                onRefetch={onRefetch}
                selection={selection}
                onClearSelection={handleClearSelection}
              />
            )}
          </RemoteDataView>
        )}
        advancedViewSettings={
          isCustomColumnsEnabled &&
          formTemplate && (
            <AdvancedViewSettings
              formTemplate={formTemplate}
              showResetToDefaultColumns={additionalColumnsIncluded}
              onResetToDefaultColumns={() => {
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.set(
                  SearchAndFilterTypes.VisibleColumns,
                  getDefaultColumnIdsForTab({
                    tabType: FormTabsTypes.DropoffForms,
                    ...defaultColumnsProps,
                  }).join(FILTER_DELIMITER)
                );
                setSearchParams(newSearchParams, { replace: true });
              }}
            />
          )
        }
        columnVisibilityPrefixesToExpand={["question-"]}
      />
    </Flex>
  );
};
